import { Component, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, ViewChild, computed, Signal, effect } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { AdminOrdersService } from '../../../services/orders/admin-orders.service';
import { LocationsService } from 'src/app/core/services/locations/locations.service';
import { ScriptService } from 'src/app/core/services/script/script.service';
import { DynamicServices } from 'src/app/features/ayen-services/services/dynamic-services/dynamic-services.service';
import { AyenMapComponent } from 'ayen-google-map';
import { City, District, Region } from 'src/app/shared/Models/locations.model';
import { AddressDataResponse } from '../../../models/order-info-model';
import { MapEvent, ServiceCoverageResponse } from '../../../models/address-details.model';
import { InspectionService } from 'src/app/core/services/inspection/inspection.service';
import { InspectorsService } from 'src/app/core/services/inspectors/inspectors.service';
import { BuildingTypeService } from 'src/app/core/services/buildingType/building-type.service';
import { OrderType } from 'src/app/models/enums/order-type.enum';
import { BuildingType } from 'src/app/models/enums/building-type.enum';
import { AyenServices } from 'src/app/core/services/ayen-services/ayen-services.service';
import { toSignal } from '@angular/core/rxjs-interop';
import * as moment from 'moment';

@Component({
    selector: 'app-address-details',
    templateUrl: './address-details.component.html',
    styleUrls: ['./address-details.component.scss'],
    standalone: false
})
export class AddressDetailsComponent implements OnInit, OnDestroy {
  @Output() address_view = new EventEmitter<{ view: string; id: number }>();
  @Input() orderId!: number;
  @Input() dynamicService: number | null = null;
  @ViewChild('ayenMap') ayenMap!: AyenMapComponent;

  // Address form
  addressFormGroup!: UntypedFormGroup;
  latitude: number = 23.8859;
  longitude: number = 45.0792;
  google_address_country: string = '';
  google_address_area: string = '';
  google_address_city: string = '';
  apiLoaded: boolean = false;
  regions: Region[] = [];
  cities: City[] = [];
  districts: District[] = [];
  isCoveredLocation: boolean = true;
  uploading: boolean = false;
  loadingAddress: boolean = false;
  private pendingMapEvent: MapEvent | null = null;
  private fullscreenChangeHandler = () => {
    if (!document.fullscreenElement && this.pendingMapEvent) {
      this.ngZone.run(() => {
        this.callAddressApi(this.pendingMapEvent!);
        this.pendingMapEvent = null;
      });
    }
  };
  showAlertMsg: boolean = false;
  alertMessage: string = 'برجاء ادخال الحقول المطلوبة';
  order!: AddressDataResponse;

  // Inspection form
  inspectionDetailsFormGroup!: UntypedFormGroup;
  inspectionPeriods: any;
  inspectionPeriodsLength: any;
  inspectionAlertMsg: boolean = false;
  inspectionAlertMessage: string = 'برجاء ادخال الحقول المطلوبة';
  formatedDate: any;
  selectedPeriods: any[] = [];
  orderLoaded: boolean = false;
  disableDate: boolean = false;
  minDate = new Date();
  disableSelect: boolean = false;
  dateLabel = 'تاريخ الفحص';
  datePlaceholder = 'يرجى تحديد تاريخ الفحص';
  orderType = OrderType;
  buildingType = BuildingType;
  acadamicMajor: any;
  buildingStatus: any;
  clientGoals: any;

  // Feature flag for showing additional fields
  private featureFlags = toSignal(this.ayenServices.featureFlags$, { initialValue: null });
  
  showAdditionalFields: Signal<boolean> = computed(() => {
    const flags = this.featureFlags();
    if (!flags || !Array.isArray(flags)) return false;
    const flag = flags.find(item => item.key === 'ADMIN__PROPERY_UNIT');
    return flag?.is_enabled === true;
  });

  constructor(
    private formBuilder: UntypedFormBuilder,
    private orderService: AdminOrdersService,
    private location: LocationsService,
    private GoogleMapLoader: ScriptService,
    private dynamicServices: DynamicServices,
    private inspectionService: InspectionService,
    private inspectorService: InspectorsService,
    private buildingTypeService: BuildingTypeService,
    private ayenServices: AyenServices,
    private ngZone: NgZone,
  ) {
    // Update address field validators when feature flag changes
    effect(() => {
      const shouldHideAddressFields = this.showAdditionalFields();
      if (this.addressFormGroup) {
        const addressAreaControl = this.addressFormGroup.get('address_area');
        const addressCityControl = this.addressFormGroup.get('address_city');
        const districtControl = this.addressFormGroup.get('district');
        
        if (shouldHideAddressFields) {
          // Remove required validators when address fields are hidden
          addressAreaControl?.clearValidators();
          addressCityControl?.clearValidators();
          districtControl?.clearValidators();
        } else {
          // Add required validators when address fields are shown
          addressAreaControl?.setValidators([Validators.required]);
          addressCityControl?.setValidators([Validators.required]);
          districtControl?.setValidators([Validators.required]);
        }
        
        addressAreaControl?.updateValueAndValidity();
        addressCityControl?.updateValueAndValidity();
        districtControl?.updateValueAndValidity();
      }
    });
  }

  ngOnInit(): void {
    document.addEventListener('fullscreenchange', this.fullscreenChangeHandler);
    
    // Only initialize address-related features if address form will be shown
    if (!this.showAdditionalFields()) {
      this.GoogleMapLoader.obsCurrentApiStatus.subscribe((status: Boolean) => {
        this.apiLoaded = status.valueOf();
      });
      this.getRegions();
    }
    
    this.createForm();
    this.createInspectionForm();
    this.getInspectionPeriods();
    this.getClientGoals();
    if (this.orderId) {
      this.getAdminOrder();
    }
  }

  createForm(): void {
    // Initially set validators based on current feature flag state
    const shouldHideAddressFields = this.showAdditionalFields();
    const addressValidators = shouldHideAddressFields ? [] : [Validators.required];
    
    this.addressFormGroup = this.formBuilder.group({
      address_area: [null, addressValidators],
      address_city: [null, addressValidators],
      address_unit_code: [null],
      address_street: [null],
      district: [null, addressValidators],
      short_address: [null, Validators.pattern(/^[A-Z]{4}[0-9]{4}$/)],
      landmarks: [null],
      address_building_code: [null],
    });

    this.addressFormGroup.valueChanges.subscribe(() => {
      if (this.showAlertMsg && !this.showAdditionalFields()) {
        const areaValid = this.addressFormGroup.get('address_area')?.valid;
        const cityValid = this.addressFormGroup.get('address_city')?.valid;
        const districtValid = this.addressFormGroup.get('district')?.valid;
        if (areaValid && cityValid && districtValid) {
          this.showAlertMsg = false;
        }
      }
    });
  }

  createInspectionForm(): void {
    this.inspectionDetailsFormGroup = this.formBuilder.group({
      orderDate: [null, Validators.required],
      is_inspection_later: [false],
      order_periods: [null, Validators.required],
      notes: [null],
      order_date: [null],
      inspector_academic_major: [null],
      building_status: [null],
      client_goal: [null, Validators.required],
      floors_number: [null],
    });
  }

  getAdminOrder(): void {
    this.orderService.getAdminOrder(this.orderId).subscribe(
      (data: AddressDataResponse) => {
        this.order = data;
        this.orderLoaded = true;
        if (this.dynamicService == null && data.dynamic_service != null) {
          this.dynamicService = data.dynamic_service;
        }
        
        // Only handle map location if address form is visible
        if (!this.showAdditionalFields() && data.location) {
          const loc = data.location.split(',');
          this.latitude = +loc[0];
          this.longitude = +loc[1];
          this.ayenMap?.reCenterMap(this.latitude, this.longitude);
        }
        
        if (this.order.order_type === this.orderType.Visit) {
          this.getAcadamicMajor();
          this.getBuildingStatus();
        }
        this.patchValues();
        this.patchInspectionValues();
      },
      () => {},
    );
  }

  patchValues(): void {
    // Only patch address form if it's visible
    if (this.showAdditionalFields()) {
      return;
    }
    
    const areaId = this.order.address_area?.id ?? null;
    const cityId = this.order.address_city?.id ?? null;

    this.addressFormGroup.patchValue({
      address_area: areaId,
      address_city: cityId,
      address_unit_code: this.order.address_unit_code,
      address_street: this.order.address_street,
      district: this.order.district?.id ?? null,
      short_address: this.order.short_address,
      landmarks: this.order.landmark,
      address_building_code: this.order.address_building_code,
    });

    if (areaId) this.getCitiesByRegion(areaId);
    if (cityId) this.getDistrictsByCity(cityId);
    this.isCoveredLocation = this.order.is_located_in_covered_cities;
  }

  patchInspectionValues(): void {
    const order = this.order as any;
    this.selectedPeriods = order.order_periods ?? [];
    this.inspectionDetailsFormGroup.patchValue({
      orderDate: order.order_date ? moment(order.order_date) : null,
      is_inspection_later: order.is_inspection_later,
      order_periods: order.order_periods,
      notes: order.notes,
      inspector_academic_major: order.inspector_academic_major,
      building_status: order.building_status ?? null,
      client_goal: order.client_goal ?? null,
      floors_number: order.floors_number ?? null,
    });
    this.disableDate = order.is_inspection_later;
    setTimeout(() => { this.updateDate(); });
  }

  // --- Address methods ---

  regionChanged(): void {
    this.addressFormGroup.get('address_city')?.setValue(null);
    this.addressFormGroup.get('district')?.setValue(null);
    this.districts = [];
    const regionId = this.addressFormGroup.get('address_area')?.value as number | null;
    if (regionId) {
      this.getCitiesByRegion(regionId);
    }
  }

  checkServiceCoverage(): void {
    const cityId = this.addressFormGroup.get('address_city')?.value as number | null;
    const serviceId = this.dynamicService ?? (this.order as any)?.dynamic_service;

    if (cityId) {
      this.addressFormGroup.get('district')?.setValue(null);
      this.getDistrictsByCity(cityId);
    }

    if (!cityId || !serviceId) return;

    this.dynamicServices
      .checkServiceCoverage({ city_id: cityId, service_id: serviceId })
      .subscribe((data: ServiceCoverageResponse) => {
        this.isCoveredLocation = data.is_covered;
      });
  }

  mapValues(event: MapEvent): void {
    this.pendingMapEvent = event;
  }

  private callAddressApi(event: MapEvent): void {
    this.google_address_city = event.city ?? '';
    this.google_address_country = event.country;
    this.google_address_area = event.state;
    this.latitude = event.latitude;
    this.longitude = event.longitude;
    this.loadingAddress = true;
    const location = `${event.latitude},${event.longitude}`;
    this.orderService.getAddressData(this.orderId, location).subscribe(
      (data: AddressDataResponse) => {
        const areaId = data.address_area?.id ?? null;
        const cityId = data.address_city?.id ?? null;

        this.addressFormGroup.patchValue({
          address_area: areaId,
          address_city: cityId,
          address_unit_code: data.address_unit_code,
          address_street: data.address_street,
          district: data.district?.id ?? null,
          short_address: data.short_address,
          landmarks: data.landmark,
          address_building_code: data.address_building_code,
        });

        this.isCoveredLocation = data.is_located_in_covered_cities;

        if (areaId) this.getCitiesByRegion(areaId);
        if (cityId) this.getDistrictsByCity(cityId);

        this.addressFormGroup.markAllAsTouched();
        const hasEmptyRequired = !areaId || !cityId || !this.addressFormGroup.get('district')?.value;
        if (hasEmptyRequired) {
          this.showAlertMsg = true;
          this.alertMessage = 'لم يتم التعرف على العنوان تلقائياً، يرجى إدخال الحقول المطلوبة يدوياً';
        }
        this.loadingAddress = false;
      },
      () => { this.loadingAddress = false; },
    );
  }

  ngOnDestroy(): void {
    document.removeEventListener('fullscreenchange', this.fullscreenChangeHandler);
  }

  getRegions(): void {
    this.location.getAllRegions().subscribe((data: Region[]) => { this.regions = data; });
  }

  getCitiesByRegion(regionId: number): void {
    this.location.getCitiesByRegionPK(regionId).subscribe((data: City[]) => { this.cities = data; });
  }

  getDistrictsByCity(cityId: number): void {
    this.location.getDistrictsByCityPK(cityId).subscribe((data: District[]) => {
      this.districts = data;
      if (data.length === 0) {
        this.addressFormGroup.get('district')?.clearValidators();
        this.addressFormGroup.get('district')?.setValue(null);
        this.addressFormGroup.get('district')?.updateValueAndValidity();
      } else {
        this.addressFormGroup.get('district')?.setValidators([Validators.required]);
        this.addressFormGroup.get('district')?.updateValueAndValidity();
      }
    });
  }

  onShortAddressInput(): void {
    const control = this.addressFormGroup.get('short_address');
    if (control?.value) {
      control.setValue((control.value as string).toUpperCase(), { emitEvent: false });
    }
  }

  // --- Inspection methods ---
  getInspectionPeriods(): void {
    this.inspectionService.getinspectionPeriods().subscribe((data) => {
      this.inspectionPeriods = data;
      this.inspectionPeriodsLength = data.length;
    });
  }

  setOrderPeriods(event: any): void {
    this.inspectionDetailsFormGroup.controls['order_periods'].patchValue([...event]);
  }

  toggleDisableDate(event: any): void {
    this.disableDate = event.target.checked;
    this.disableSelect = event.target.checked;
    this.updateDate();
  }

  updateDate(): void {
    const items = document.querySelectorAll('.red');
    const order = this.order as any;
    if (this.disableDate) {
      this.clearInspectionControl('orderDate', null);
      this.selectedPeriods = [];
      this.clearInspectionControl('order_periods', this.selectedPeriods);
      items.forEach((item) => item.classList.add('remove-validation'));
    } else {
      this.inspectionDetailsFormGroup.controls['orderDate'].setValue(
        order?.order_date ? moment(order.order_date) : null,
      );
      this.selectedPeriods = order?.order_periods ?? [];
      this.setOrderPeriods(this.selectedPeriods);
      items.forEach((item) => item.classList.remove('remove-validation'));
    }
  }

  clearInspectionControl(formControl: string, newVal: any): void {
    this.inspectionDetailsFormGroup.controls[formControl].setValue(newVal);
    this.inspectionDetailsFormGroup.controls[formControl].clearValidators();
    this.inspectionDetailsFormGroup.controls[formControl].updateValueAndValidity();
  }

  getAcadamicMajor(): void {
    this.inspectorService.getAcadamicMajor().subscribe((data) => { this.acadamicMajor = data; });
  }

  getBuildingStatus(): void {
    this.buildingTypeService.getBuildingStatus().subscribe((data) => { this.buildingStatus = data; });
  }

  getClientGoals(): void {
    this.orderService.getClientGoals().subscribe((data) => { this.clientGoals = data; });
  }

  // --- Save ---

  save(): void {
    // Only validate address form if it's visible (showAdditionalFields is false)
    const shouldValidateAddress = !this.showAdditionalFields();
    
    const addressFormInvalid = shouldValidateAddress &&
      this.addressFormGroup.status === 'INVALID' &&
      Object.entries(this.addressFormGroup.controls).some(
        ([key, ctrl]) => key !== 'short_address' && ctrl.invalid,
      );
    if (addressFormInvalid) {
      this.showAlertMsg = true;
      const fields = document.querySelectorAll('ng-select.ng-invalid, input.ng-invalid');
      fields.forEach((field) => field.parentElement?.classList.add('invalid-field'));
      return;
    }
    if (this.inspectionDetailsFormGroup.status === 'INVALID') {
      this.inspectionAlertMsg = true;
      const fields = document.querySelectorAll('.ng-invalid .mat-form-field-flex');
      fields.forEach((field) => field.classList.add('invalid-field'));
      return;
    }
    this.uploading = true;

    // Only include address data if address form is visible (feature flag is disabled)
    const shouldIncludeAddress = !this.showAdditionalFields();
    let addressPayload = {};
    
    if (shouldIncludeAddress) {
      const formValue = this.addressFormGroup.value;
      addressPayload = {
        ...formValue,
        landmark: formValue.landmarks ?? null,
        landmarks: undefined,
        google_address_city: this.google_address_city,
        google_address_area: this.google_address_area,
        google_address_country: this.google_address_country,
        location: `${this.latitude},${this.longitude}`,
      };
    }

    const inspectionValue = { ...this.inspectionDetailsFormGroup.value };
    if (this.inspectionDetailsFormGroup.get('orderDate')?.value) {
      this.formatedDate = moment(this.inspectionDetailsFormGroup.get('orderDate')!.value).format('YYYY-MM-DD');
      inspectionValue.order_date = this.formatedDate;
    }

    this.orderService.updateOrder(this.orderId, { ...addressPayload, ...inspectionValue }).subscribe(
      (data: { id: number }) => {
        this.uploading = false;
        this.address_view.emit({ view: 'summary', id: data.id });
      },
      (error: { error: Record<string, unknown[]> }) => {
        try {
          this.showAlertMsg = true;
          this.alertMessage = Object.values(error.error)[0].toString();
        } catch {}
        this.uploading = false;
      },
    );
  }

  prevStep(): void {
    this.address_view.emit({ view: 'building', id: this.orderId });
  }

  hideErrorMsg(): void {
    this.showAlertMsg = false;
    this.inspectionAlertMsg = false;
  }
}
